import React, { useCallback, useEffect, useState } from "react";
import { ownerApi, api, uploadImage, buildFileUrl, errMsg } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { Plus, Check, Upload, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "./ui/alert-dialog";

const inp = "h-10 border-white/10 bg-black/30 text-white";
const card = "rounded-2xl border border-white/10 bg-[#131822] p-5 space-y-3";

export function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-slate-400">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

export function LogoInput({ value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const data = await uploadImage(f, ownerApi, "/owner/upload");
      onChange(data.url);
    } catch (err) {
      toast.error(errMsg(err, "Upload failed"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-3">
      {value && <img src={buildFileUrl(value)} alt="logo" className="h-10 w-10 rounded-lg object-cover" />}
      <label className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-white/10 px-3 py-2 text-xs hover:bg-white/5">
        <Upload className="h-3 w-3" /> {busy ? "Uploading…" : value ? "Change logo" : "Upload logo"}
        <input type="file" accept="image/*" className="hidden" onChange={pick} data-testid={testid} />
      </label>
    </div>
  );
}

function GeneralSettings({ pub, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => {
    setF({ name: pub.name, logo_url: pub.logo_url, active: pub.active, admin_email: pub.admin_email || "", dj_email: pub.dj_email || "", commission_pct: pub.commission_pct });
  }, [pub]);
  const save = async () => {
    try {
      await ownerApi.patch(`/owner/pubs/${pub.id}`, { ...f, commission_pct: Number(f.commission_pct) });
      toast.success("Pub settings saved");
      onSaved?.();
    } catch (e) {
      toast.error(errMsg(e, "Save failed"));
    }
  };
  return (
    <div className={card} data-testid="pub-general-settings">
      <div className="flex items-center justify-between">
        <p className="font-bold">General</p>
        <label className="flex items-center gap-2 text-xs text-slate-300">
          Active <Switch checked={!!f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="pub-active-switch" />
        </label>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Name"><Input className={inp} value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="pub-name-input" /></Field>
        <Field label="Commission %"><Input className={inp} type="number" min={0} max={100} value={f.commission_pct ?? ""} onChange={(e) => setF({ ...f, commission_pct: e.target.value })} data-testid="pub-commission-input" /></Field>
        <Field label="Admin email"><Input className={inp} value={f.admin_email || ""} onChange={(e) => setF({ ...f, admin_email: e.target.value })} data-testid="pub-admin-email-input" /></Field>
        <Field label="DJ email"><Input className={inp} value={f.dj_email || ""} onChange={(e) => setF({ ...f, dj_email: e.target.value })} data-testid="pub-dj-email-input" /></Field>
      </div>
      <Field label="Logo"><LogoInput value={f.logo_url} onChange={(u) => setF({ ...f, logo_url: u })} testid="pub-logo-file" /></Field>
      <Button onClick={save} className="bg-violet-500 hover:bg-violet-600" data-testid="pub-save-button">Save</Button>
    </div>
  );
}

function PubCodeReset({ pub }) {
  const [code, setCode] = useState("");
  const save = async () => {
    try {
      await ownerApi.post(`/owner/pubs/${pub.id}/code`, { code });
      setCode("");
      toast.success("Pub code updated");
    } catch (e) {
      toast.error(errMsg(e, "Update failed"));
    }
  };
  return (
    <div className="flex items-end gap-2">
      <div className="flex-1">
        <Field label="New pub code (min 6)">
          <Input type="password" autoComplete="off" className={inp} value={code} onChange={(e) => setCode(e.target.value)} data-testid="pub-code-input" />
        </Field>
      </div>
      <Button onClick={save} variant="ghost" className="border border-white/10" data-testid="pub-code-save">Set</Button>
    </div>
  );
}

function PasswordReset({ pub }) {
  const [pw, setPw] = useState({ admin: "", dj: "" });
  const reset = async (role) => {
    try {
      await ownerApi.post(`/owner/pubs/${pub.id}/password`, { role, password: pw[role] });
      setPw({ ...pw, [role]: "" });
      toast.success(`${role === "dj" ? "DJ" : "Admin"} password reset. Old sessions are signed out.`);
    } catch (e) {
      toast.error(errMsg(e, "Reset failed"));
    }
  };
  return (
    <div className={card} data-testid="pub-password-reset">
      <p className="font-bold">Pub code & passwords</p>
      <PubCodeReset pub={pub} />
      {["admin", "dj"].map((role) => (
        <div key={role} className="flex items-end gap-2">
          <div className="flex-1">
            <Field label={`New ${role} password (min 6)`}>
              <Input type="password" className={inp} value={pw[role]} onChange={(e) => setPw({ ...pw, [role]: e.target.value })} data-testid={`pub-${role}-password-input`} />
            </Field>
          </div>
          <Button onClick={() => reset(role)} variant="ghost" className="border border-white/10" data-testid={`pub-${role}-password-reset`}>Reset</Button>
        </div>
      ))}
    </div>
  );
}

function PackagePrices({ pub }) {
  const [pkgs, setPkgs] = useState([]);
  const load = useCallback(() => ownerApi.get("/owner/packages", { params: { pub_id: pub.id } }).then(({ data }) => setPkgs(data)), [pub.id]);
  useEffect(() => { load(); }, [load]);
  const save = async (p, patch) => {
    try {
      await ownerApi.patch(`/owner/packages/${p.id}`, patch);
      toast.success(`${p.name} ${patch.active === undefined ? "price saved" : patch.active ? "turned ON" : "turned OFF"}`);
      if (patch.active !== undefined) setPkgs(pkgs.map((x) => (x.id === p.id ? { ...x, active: patch.active } : x)));
    } catch (e) {
      toast.error(errMsg(e, "Save failed"));
    }
  };
  return (
    <div className={card} data-testid="pub-package-prices">
      <p className="font-bold">Shout-out package prices</p>
      {pkgs.map((p, i) => (
        <div key={p.id} className="flex items-center gap-2">
          <Switch checked={p.active !== false} onCheckedChange={(v) => save(p, { active: v })} data-testid={`package-toggle-${p.tier}`} />
          <span className={`w-40 text-sm ${p.active === false ? "text-slate-500 line-through" : ""}`}>{p.name}</span>
          <Input type="number" min={1} className={`${inp} w-28`} value={p.price}
            onChange={(e) => setPkgs(pkgs.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)))} data-testid={`package-price-${p.tier}`} />
          <Button size="sm" onClick={() => save(p, { price: Number(p.price) })} className="bg-violet-500" data-testid={`package-save-${p.tier}`}><Check className="h-3 w-3" /></Button>
        </div>
      ))}
    </div>
  );
}

function PaymentKeys({ pub, onSaved }) {
  const [f, setF] = useState({ key_id: "", key_secret: "", webhook_secret: "" });
  const save = async () => {
    try {
      await ownerApi.post(`/owner/pubs/${pub.id}/razorpay-keys`, f);
      setF({ key_id: "", key_secret: "", webhook_secret: "" });
      toast.success("Payment keys saved (encrypted)");
      onSaved?.();
    } catch (e) {
      toast.error(errMsg(e, "Save failed"));
    }
  };
  const remove = async () => {
    try {
      await ownerApi.post(`/owner/pubs/${pub.id}/razorpay-keys`, { remove_all: true });
      toast.success("Payment keys removed — default keys will be used");
      onSaved?.();
    } catch (e) {
      toast.error(errMsg(e, "Remove failed"));
    }
  };
  return (
    <div className={card} data-testid="pub-payment-keys">
      <p className="font-bold">Edit Payment Keys (Razorpay)</p>
      <p className="text-xs text-slate-400">
        Key ID: <span className="text-slate-200" data-testid="rzp-key-id">{pub.rzp_key_id || "Using default keys"}</span>
        {pub.rzp_key_secret_hint && <> · Secret: <span data-testid="rzp-key-secret-hint">{pub.rzp_key_secret_hint}</span></>}
        {pub.rzp_webhook_secret_hint && <> · Webhook: <span data-testid="rzp-webhook-hint">{pub.rzp_webhook_secret_hint}</span></>}
      </p>
      <Field label="Razorpay Key ID"><Input className={inp} autoComplete="off" value={f.key_id} onChange={(e) => setF({ ...f, key_id: e.target.value })} placeholder="rzp_live_…" data-testid="rzp-key-id-input" /></Field>
      <Field label="Key Secret (stored encrypted, never shown)"><Input type="password" autoComplete="new-password" className={inp} value={f.key_secret} onChange={(e) => setF({ ...f, key_secret: e.target.value })} placeholder="Leave blank to keep" data-testid="rzp-key-secret-input" /></Field>
      <Field label="Webhook Secret (stored encrypted, never shown)"><Input type="password" autoComplete="new-password" className={inp} value={f.webhook_secret} onChange={(e) => setF({ ...f, webhook_secret: e.target.value })} placeholder="Leave blank to keep" data-testid="rzp-webhook-input" /></Field>
      <div className="flex gap-2">
        <Button onClick={save} className="bg-violet-500" data-testid="rzp-save">Save</Button>
        {pub.rzp_key_id && <Button variant="ghost" className="border border-white/10" onClick={remove} data-testid="rzp-remove">Remove (use defaults)</Button>}
      </div>
    </div>
  );
}

function DeletePub({ pub, onSaved }) {
  const [confirm, setConfirm] = useState("");
  const del = async () => {
    try {
      await ownerApi.delete(`/owner/pubs/${pub.id}`, { data: { confirm } });
      toast.success("Pub deleted");
      onSaved?.();
    } catch (e) {
      toast.error(errMsg(e, "Delete failed"));
    }
  };
  return (
    <div className="rounded-2xl border border-pink-500/30 bg-pink-500/5 p-5" data-testid="pub-delete-card">
      <p className="font-bold text-pink-200">Delete this pub</p>
      <p className="mt-1 text-xs text-slate-400">The pub disappears everywhere and its staff are signed out. Payment history is kept.</p>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="ghost" className="mt-3 border border-pink-500/40 text-pink-300 hover:bg-pink-500/10" data-testid="pub-delete-open">
            <Trash2 className="mr-1 h-3 w-3" /> Delete pub
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent className="border-white/10 bg-[#131822] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {pub.name}?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Type <b className="text-white">{pub.name}</b> to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input className="border-white/10 bg-black/30 text-white" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="pub-delete-confirm" />
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 bg-transparent hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} disabled={confirm !== pub.name} className="bg-pink-500 hover:bg-pink-600" data-testid="pub-delete-submit">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function PubSettings({ pub, onSaved, onDeleted }) {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <GeneralSettings pub={pub} onSaved={onSaved} />
        <PasswordReset pub={pub} />
        <PackagePrices pub={pub} />
        <PaymentKeys pub={pub} onSaved={onSaved} />
      </div>
      <DeletePub pub={pub} onSaved={onDeleted || onSaved} />
    </div>
  );
}
